import { useEffect, useState } from 'react';
import { AccessibilityInfo, Platform } from 'react-native';

/**
 * Whether the user has asked the system for less motion. Web reads
 * `prefers-reduced-motion` directly (CSS motion reads the same media query
 * via MOTION_CSS_VARS — this hook is for the RN `Animated` call sites CSS
 * can't reach, e.g. DockedPanel's slide-in); native reads
 * AccessibilityInfo's reduce-motion setting. Defaults to `false` until the
 * first check resolves, matching how everything already animates today.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (Platform.OS === 'web') {
      if (typeof window === 'undefined' || !window.matchMedia) return;
      const query = window.matchMedia('(prefers-reduced-motion: reduce)');
      setReduced(query.matches);
      const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
      // Safari < 14 lacks addEventListener on MediaQueryList; addListener is
      // the deprecated-but-supported fallback.
      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
      }
      query.addListener(onChange);
      return () => query.removeListener(onChange);
    }

    let cancelled = false;
    AccessibilityInfo.isReduceMotionEnabled?.()
      .then((value) => {
        if (!cancelled) setReduced(value);
      })
      .catch(() => {});
    const subscription = AccessibilityInfo.addEventListener?.('reduceMotionChanged', (value) => {
      setReduced(value);
    });
    return () => {
      cancelled = true;
      subscription?.remove?.();
    };
  }, []);

  return reduced;
}
